import { ISO_WEEKDAYS } from '@brightlocal/shared';

import type { ScheduleFrequency, ScheduleTimeSlot } from './types';

/** How often a report updates, as the schedule form holds it; each tool maps it to its own API value. */
export const SCHEDULE_FREQUENCY = {
  MANUAL: 'manual',
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly'
} as const;

/** Start times (HH:MM:SS) of the eight three-hour windows in the Time of day select. */
export const SCHEDULE_TIME_SLOTS = [
  '00:00:00',
  '03:00:00',
  '06:00:00',
  '09:00:00',
  '12:00:00',
  '15:00:00',
  '18:00:00',
  '21:00:00'
] as const;

/** Days the Date select lists, capped at 28 so every month has them. */
export const SCHEDULE_MONTH_DAYS = Array.from({ length: 28 }, (_, index) => index + 1);

/** Schedule form field names, matching the report settings API fields. */
export const SCHEDULE_FORM_FIELDS = {
  FREQUENCY: 'scheduleFrequency',
  RUN_ON: 'scheduleRunOn',
  TIME_SLOT: 'scheduleTimeSlot'
} as const;

/** Day a weekly schedule gets when the user switches to Weekly. */
export const DEFAULT_WEEKLY_RUN_ON = ISO_WEEKDAYS.MONDAY;

/** Date a monthly schedule gets when the user switches to Monthly. */
export const DEFAULT_MONTHLY_RUN_ON = 1;

/** Time slot a schedule gets when it has none yet. */
export const DEFAULT_TIME_SLOT = SCHEDULE_TIME_SLOTS[0];

/** Height of the Date dropdown, which scrolls through its 28 days and the last-day option. */
export const DATE_SELECT_MAX_HEIGHT = 264;

/** i18n label keys of each frequency in the How often select, in display order. Daily is not offered. */
export const FREQUENCY_LABEL_KEYS = {
  manual: 'reportSchedule.frequency.options.manual',
  weekly: 'reportSchedule.frequency.options.weekly',
  monthly: 'reportSchedule.frequency.options.monthly'
} as const satisfies Record<Exclude<ScheduleFrequency, typeof SCHEDULE_FREQUENCY.DAILY>, string>;

/** How often options a report gets when its tool doesn't narrow them, in display order. */
export const OFFERED_FREQUENCIES = [
  SCHEDULE_FREQUENCY.MANUAL,
  SCHEDULE_FREQUENCY.WEEKLY,
  SCHEDULE_FREQUENCY.MONTHLY
] as const satisfies readonly (keyof typeof FREQUENCY_LABEL_KEYS)[];

/** Options of the Day select — ISO weekday values with their i18n label keys, Monday first. */
export const WEEKDAY_OPTIONS = [
  { value: ISO_WEEKDAYS.MONDAY, labelKey: 'reportSchedule.day.options.monday' },
  { value: ISO_WEEKDAYS.TUESDAY, labelKey: 'reportSchedule.day.options.tuesday' },
  { value: ISO_WEEKDAYS.WEDNESDAY, labelKey: 'reportSchedule.day.options.wednesday' },
  { value: ISO_WEEKDAYS.THURSDAY, labelKey: 'reportSchedule.day.options.thursday' },
  { value: ISO_WEEKDAYS.FRIDAY, labelKey: 'reportSchedule.day.options.friday' },
  { value: ISO_WEEKDAYS.SATURDAY, labelKey: 'reportSchedule.day.options.saturday' },
  { value: ISO_WEEKDAYS.SUNDAY, labelKey: 'reportSchedule.day.options.sunday' }
] as const;

/** i18n label keys of each three-hour window in the Time of day select. */
export const TIME_SLOT_LABEL_KEYS = {
  '00:00:00': 'reportSchedule.timeSlot.options.slot00',
  '03:00:00': 'reportSchedule.timeSlot.options.slot03',
  '06:00:00': 'reportSchedule.timeSlot.options.slot06',
  '09:00:00': 'reportSchedule.timeSlot.options.slot09',
  '12:00:00': 'reportSchedule.timeSlot.options.slot12',
  '15:00:00': 'reportSchedule.timeSlot.options.slot15',
  '18:00:00': 'reportSchedule.timeSlot.options.slot18',
  '21:00:00': 'reportSchedule.timeSlot.options.slot21'
} as const satisfies Record<ScheduleTimeSlot, string>;
